/**
 * useEmailScanning — Per-user OAuth email connections
 *
 * Two hooks:
 *   useEmailScanning() — Settings page: connect/disconnect/scan
 *   useProjectMatchedEmails(projectId) — Project Activity tab: matched emails
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type {
  EmailScanConnection,
  EmailProviderType,
  EmailIngestionAudit,
  MatchedEmail,
} from '@/types/emailScanning';

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

const OAUTH_ALLOWED_HOSTS: Record<EmailProviderType, string[]> = {
  microsoft: ['login.microsoftonline.com'],
  google: ['accounts.google.com'],
};

export function getValidatedOAuthUrl(authUrl: string | undefined, provider: EmailProviderType): string {
  if (!authUrl) {
    throw new Error('Email provider did not return an OAuth URL');
  }

  const url = new URL(authUrl);
  if (url.protocol !== 'https:' || !OAUTH_ALLOWED_HOSTS[provider].includes(url.hostname)) {
    throw new Error('Email provider returned an unexpected OAuth URL');
  }

  return url.toString();
}

interface OAuthLocationLike {
  href: string;
  assign: (url: string) => void;
}

export function navigateToOAuthProvider(
  authUrl: string,
  locationRef: OAuthLocationLike = window.location,
  setTimer: typeof window.setTimeout = window.setTimeout
): void {
  locationRef.assign(authUrl);

  // Safari occasionally leaves the page in place after an async callable
  // resolves. Retry the same top-level navigation instead of leaving the
  // connect button disabled indefinitely.
  setTimer(() => {
    if (locationRef.href !== authUrl) {
      locationRef.href = authUrl;
    }
  }, 250);
}

// ============================================================================
// SETTINGS PAGE HOOK
// ============================================================================

export function useEmailScanning() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<EmailScanConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [loading, setLoading] = useState(false);

  // Real-time listener for user's connections
  useEffect(() => {
    if (!user?.uid) {
      setConnections([]);
      setLoadingConnections(false);
      return;
    }

    const q = query(
      collection(db, 'emailScanConnections'),
      where('userId', '==', user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const conns: EmailScanConnection[] = snap.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            userId: data.userId,
            organizationId: data.organizationId,
            emailAddress: data.emailAddress,
            provider: data.provider,
            status: data.status,
            errorMessage: data.errorMessage,
            lastScanAt: data.lastScanAt?.toDate?.() || undefined,
            stats: data.stats || {
              totalScans: 0,
              totalEmailsFound: 0,
              totalMatches: 0,
              lastEmailsFound: 0,
              lastMatches: 0,
            },
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
          } as EmailScanConnection;
        });
        setConnections(conns);
        setLoadingConnections(false);
      },
      (error) => {
        console.error('[useEmailScanning] Listener error:', error);
        setLoadingConnections(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  // Check URL params for OAuth callback result (once only)
  const oauthProcessed = useRef(false);
  useEffect(() => {
    if (oauthProcessed.current) return;

    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const tab = params.get('tab');

    if (tab === 'email' && status) {
      oauthProcessed.current = true;

      if (status === 'connected') {
        const email = params.get('email') || '';
        const provider = params.get('provider') || '';
        toast.success(`Connected ${provider === 'microsoft' ? 'Microsoft 365' : 'Google'} email: ${email}`);
      } else if (status === 'error') {
        const message = params.get('message') || 'Failed to connect email';
        toast.error(message);
      }

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('status');
      url.searchParams.delete('tab');
      url.searchParams.delete('provider');
      url.searchParams.delete('email');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const connectEmail = useCallback(async (provider: EmailProviderType) => {
    setLoading(true);
    try {
      const fn = httpsCallable<{ provider: string; returnUrl: string }, { authUrl: string }>(
        functions,
        'initiateEmailOAuth',
        { timeout: 120_000 }
      );
      const result = await fn({ provider, returnUrl: window.location.origin });
      const authUrl = getValidatedOAuthUrl(result.data?.authUrl, provider);

      // Redirect to Microsoft/Google login
      navigateToOAuthProvider(authUrl);
    } catch (err: unknown) {
      console.error('[useEmailScanning] Connect error:', err);
      const message = getErrorMessage(err, 'Failed to start email connection');
      toast.error(message);
      setLoading(false);
    }
  }, []);

  const disconnectEmail = useCallback(async (connectionId: string) => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'disconnectEmail');
      await fn({ connectionId });
      toast.success('Email disconnected');
    } catch (err: unknown) {
      console.error('[useEmailScanning] Disconnect error:', err);
      toast.error(getErrorMessage(err, 'Failed to disconnect'));
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerManualScan = useCallback(async (connectionId: string) => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'triggerEmailScan', { timeout: 540_000 });
      await fn({ connectionId });
      toast.success('Scan completed');
    } catch (err: unknown) {
      console.error('[useEmailScanning] Scan error:', err);
      toast.error(getErrorMessage(err, 'Failed to start scan'));
    } finally {
      setLoading(false);
    }
  }, []);

  const reconnectEmail = useCallback(async (provider: EmailProviderType) => {
    // Same as connect — the backend handles the reconnect case
    await connectEmail(provider);
  }, [connectEmail]);

  return {
    connections,
    loadingConnections,
    loading,
    connectEmail,
    disconnectEmail,
    triggerManualScan,
    reconnectEmail,
  };
}

export function useEmailIngestionAudit() {
  const { user } = useAuth();
  const [audit, setAudit] = useState<EmailIngestionAudit | null>(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const refreshAudit = useCallback(async () => {
    if (!user?.uid) {
      setAudit(null);
      setAuditError(null);
      return;
    }

    setLoadingAudit(true);
    setAuditError(null);
    try {
      const fn = httpsCallable<undefined, EmailIngestionAudit>(
        functions,
        'getEmailIngestionAudit',
        { timeout: 60_000 }
      );
      const result = await fn(undefined);
      setAudit(result.data);
    } catch (err: unknown) {
      console.error('[useEmailIngestionAudit] Audit load error:', err);
      setAuditError(getErrorMessage(err, 'Failed to load email ingestion audit'));
    } finally {
      setLoadingAudit(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    void refreshAudit();
  }, [refreshAudit]);

  return {
    audit,
    loadingAudit,
    auditError,
    refreshAudit,
  };
}

// ============================================================================
// PROJECT MATCHED EMAILS HOOK
// ============================================================================

export function useProjectMatchedEmails(projectId: string) {
  const [matchedEmails, setMatchedEmails] = useState<MatchedEmail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setMatchedEmails([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'projects', projectId, 'matchedEmails'),
      where('dismissed', '==', false),
      orderBy('receivedAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const emails: MatchedEmail[] = snap.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            from: data.from,
            to: data.to || [],
            cc: data.cc || [],
            subject: data.subject,
            bodySnippet: data.bodySnippet,
            receivedAt: data.receivedAt?.toDate?.() || new Date(),
            hasAttachments: data.hasAttachments,
            attachments: data.attachments,
            provider: data.provider,
            projectId: data.projectId,
            projectName: data.projectName,
            matchConfidence: data.matchConfidence,
            matchSource: data.matchSource,
            matchReason: data.matchReason,
            confirmed: data.confirmed,
            dismissed: data.dismissed,
            scannedFromEmail: data.scannedFromEmail,
            connectionId: data.connectionId,
            ingestionStatus: data.ingestionStatus,
            ingestedDocuments: data.ingestedDocuments || [],
            ingestedAt: data.ingestedAt?.toDate?.(),
            portfolioIngestionStatus: data.portfolioIngestionStatus,
            portfolioIngestedAt: data.portfolioIngestedAt?.toDate?.(),
            portfolioIngestion: data.portfolioIngestion,
            classification: data.classification,
            routing: data.routing,
          } as MatchedEmail;
        });
        setMatchedEmails(emails);
        setLoading(false);
      },
      (error) => {
        console.error('[useProjectMatchedEmails] Listener error:', error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [projectId]);

  const dismissEmail = useCallback(async (matchId: string) => {
    try {
      const fn = httpsCallable(functions, 'dismissMatchedEmail');
      await fn({ matchId, projectId });
    } catch (err: unknown) {
      console.error('[useProjectMatchedEmails] Dismiss error:', err);
      toast.error('Failed to dismiss email');
    }
  }, [projectId]);

  const confirmEmail = useCallback(async (matchId: string) => {
    try {
      const fn = httpsCallable(functions, 'confirmMatchedEmail');
      await fn({ matchId, projectId });
    } catch (err: unknown) {
      console.error('[useProjectMatchedEmails] Confirm error:', err);
      toast.error('Failed to confirm email');
    }
  }, [projectId]);

  return {
    matchedEmails,
    loading,
    dismissEmail,
    confirmEmail,
  };
}
